'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Heart } from 'lucide-react';
import { toggleFavorite, useIsFavorited } from '@/lib/local-lists';

// The heart on a listing card: the one button on the photo. It saves without
// an account (the list lives on the device and joins the account at sign-in)
// and says so in a word, beside itself, for a moment.
export default function FavoriteButton({ propertyId, lang }: { propertyId: string; lang: string }) {
  const isFr = lang === 'fr';
  const saved = useIsFavorited(propertyId);
  const [notice, setNotice] = useState<string | null>(null);
  // Counts the saves made here, so the heart pops on the press and not when a page full of saved hearts is drawn.
  const [pop, setPop] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const toggle = () => {
    const nowSaved = toggleFavorite(propertyId);
    if (nowSaved) {
      setPop((n) => n + 1);
      // A tap you can feel, on the phones that can (Android); nothing for anyone who asked for less motion.
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) navigator.vibrate?.(8);
    }
    setNotice(nowSaved ? (isFr ? 'Sauvegardée' : 'Saved') : (isFr ? 'Retirée' : 'Removed'));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setNotice(null), 1600);
  };

  const label = saved
    ? (isFr ? 'Retirer des favoris' : 'Remove from favorites')
    : (isFr ? 'Ajouter aux favoris' : 'Add to favorites');

  return (
    <div className="absolute top-2 right-2 z-20 flex items-center gap-1.5">
      {/* Announced as well as shown: the heart's new state is otherwise only a colour. */}
      <span role="status" className={notice ? 'px-2.5 py-1 rounded-full bg-black/80 text-white text-xs font-semibold' : 'sr-only'}>
        {notice}
      </span>
      <button
        type="button"
        onClick={toggle}
        aria-pressed={saved}
        aria-label={label}
        title={label}
        className="w-11 h-11 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white cursor-pointer transition-transform motion-safe:active:scale-90"
      >
        <Heart key={pop} className={`w-5 h-5 ${saved ? 'fill-danger text-danger' : ''} ${saved && pop > 0 ? 'heart-pop' : ''}`} />
      </button>
    </div>
  );
}
